import { useState, useCallback } from 'react';
import { Eye, Check, ArrowRight, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';
import { GasDischargeSetup, PrismSpectrometer } from '@/components/lab';

const steps = [
  { id: 1, title: 'Switch to Helium', short: 'Install the helium discharge tube' },
  { id: 2, title: 'Observe Helium Spectrum', short: 'Note the yellow, green, and blue lines' },
  { id: 3, title: 'Switch to Neon/Argon', short: 'Install the neon or argon tube' },
  { id: 4, title: 'Observe Neon/Argon Spectrum', short: 'Count lines, note colours and spacing' },
  { id: 5, title: 'Record Observations', short: 'Compare all three spectra' }
];

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Switching to Helium',
    content: 'Helium has 2 electrons, making its spectrum more complex than hydrogen. Record the angle for all helium lines: Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak) - 7 lines total. The helium wavelengths are provided to you. Use these to determine a relationship between wavelength and recorded angle (calibration curve in Excel).'
  },
  2: {
    title: 'Helium Spectrum Analysis',
    content: 'Helium shows 7 lines across the visible spectrum: Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak). Unlike hydrogen\'s simple Balmer series, helium\'s spectrum reflects its two-electron system with singlet and triplet states.'
  },
  3: {
    title: 'Switching to Neon or Argon',
    content: 'Neon or Argon produces a complex spectrum. For this gas, you do not need to record all the readings - simply observe and get a count of all the lines, how many of each colour, how close together, etc.'
  },
  4: {
    title: 'Neon/Argon Spectrum Analysis',
    content: 'For Neon or Argon, simply observe and count all the lines, noting how many of each colour and how closely spaced they are. Each element\'s unique line pattern serves as a spectral fingerprint for identification.'
  },
  5: {
    title: 'Comparing Atomic Spectra',
    content: 'Comparing H, He, and Ne illustrates a key principle: spectrum complexity increases with electron count. Hydrogen (1 electron) gives simple series, helium (2 electrons) shows more lines, and neon (10 electrons) produces a rich spectrum. This demonstrates why emission spectroscopy is powerful for element identification.'
  }
};

type GasType = 'hydrogen' | 'helium' | 'neon';

interface Part4ComparisonProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

// Spectral characteristics
const GAS_INFO: Record<GasType, { description: string; color: string; lineCount: string }> = {
  hydrogen: {
    description: 'Simple spectrum with 4 visible Balmer lines (Red, Blue, Purple, Purple weak)',
    color: 'Pink-purple',
    lineCount: '4 lines'
  },
  helium: {
    description: 'More complex spectrum with 7 visible lines: Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak)',
    color: 'Yellow-orange',
    lineCount: '7 lines'
  },
  neon: {
    description: 'Dense spectrum - observe and count lines, note colours and spacing',
    color: 'Red-orange',
    lineCount: 'Numerous closely-spaced lines'
  }
};

export default function Part4Comparison({ onStepComplete, currentStep = 1 }: Part4ComparisonProps) {
  const [selectedGas, setSelectedGas] = useState<GasType>('hydrogen');
  const [isPowered, setIsPowered] = useState(true);
  const [heliumObserved, setHeliumObserved] = useState(false);
  const [neonObserved, setNeonObserved] = useState(false);
  const [observationsRecorded, setObservationsRecorded] = useState(false);
  const [spectrometerAngle, setSpectrometerAngle] = useState(90);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Step 1: switched to helium (selectedGas === 'helium' or beyond)
  // Step 2: helium observed
  // Step 3: switched to neon (selectedGas === 'neon')
  // Step 4: neon observed
  // Step 5: observations recorded
  const step1Done = selectedGas === 'helium' || selectedGas === 'neon' || heliumObserved;
  const step3Done = (selectedGas === 'neon' || neonObserved) && heliumObserved;
  const stepDone = [step1Done, heliumObserved, step3Done, neonObserved, observationsRecorded];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = completedCount < steps.length ? completedCount + 1 : steps.length;

  const handleSwitchToHelium = useCallback(() => {
    setSelectedGas('helium');
    setIsPowered(true);
    if (onStepComplete) onStepComplete('comparison-1');
  }, [onStepComplete]);

  const handleObserveHelium = useCallback(() => {
    setHeliumObserved(true);
    if (onStepComplete) onStepComplete('comparison-2');
  }, [onStepComplete]);

  const handleSwitchToNeon = useCallback(() => {
    setSelectedGas('neon');
    setIsPowered(true);
    if (onStepComplete) onStepComplete('comparison-3');
  }, [onStepComplete]);

  const handleObserveNeon = useCallback(() => {
    setNeonObserved(true);
    if (onStepComplete) onStepComplete('comparison-4');
  }, [onStepComplete]);

  const handleRecordObservations = useCallback(() => {
    setObservationsRecorded(true);
    if (onStepComplete) onStepComplete('comparison-5');
  }, [onStepComplete]);

  const handleSelectTube = useCallback((tube: GasType) => {
    setSelectedGas(tube);
    setIsPowered(true);
  }, []);

  return (
    <div className="exp1-part-layout">
      {/* Steps Panel */}
      <div className="exp1-steps-panel">
        <div className="exp1-steps-header">
          <ListChecks size={16} />
          <h3>Part IV: Comparison</h3>
        </div>
        <div className="exp1-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp1-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp1-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp1-step-content">
                  <span className={`exp1-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp1-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp1-lab-bench">
        <div className="exp1-bench-header">
          <div className="exp1-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp1-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp1-bench-area">
      <div className="comparison-lab">
        {/* Equipment Area */}
        <div className="equipment-row">
          <GasDischargeSetup
            selectedTube={selectedGas}
            isPowered={isPowered}
            onSelectTube={handleSelectTube}
            onTogglePower={() => setIsPowered(!isPowered)}
          />

          <PrismSpectrometer
            angle={spectrometerAngle}
            viewingSpectrum={isPowered}
            activeGas={selectedGas}
            onAngleChange={setSpectrometerAngle}
          />
        </div>

        {/* Current Gas Info */}
        <div className="gas-info-panel">
          <div className="gas-header">
            <span className="gas-name">{selectedGas.charAt(0).toUpperCase() + selectedGas.slice(1)}</span>
            <span className="gas-color" style={{
              color: selectedGas === 'hydrogen' ? '#FF69B4' :
                selectedGas === 'helium' ? '#FFA500' : '#FF4500'
            }}>
              {GAS_INFO[selectedGas].color} glow
            </span>
          </div>
          <p className="gas-description">{GAS_INFO[selectedGas].description}</p>
          <span className="line-count">{GAS_INFO[selectedGas].lineCount}</span>
        </div>

        {/* Step Instructions */}
        <div className="steps-panel">
          {/* Step 1-2: Helium */}
          {!heliumObserved && (
            <div className="step-card">
              <div className="step-header">
                <div className="step-header-left">
                  <span className="step-num">Step 1–2</span>
                  <span className="step-title">Observe Helium Spectrum</span>
                </div>
                {stepInfoContent[selectedGas !== 'helium' ? 1 : 2] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(selectedGas !== 'helium' ? 1 : 2); }}
                  >
                    ?
                  </button>
                )}
              </div>
              {selectedGas !== 'helium' ? (
                <>
                  <p>Switch to the helium discharge tube to observe its spectrum.</p>
                  <button className="action-btn" onClick={handleSwitchToHelium}>
                    <ArrowRight size={16} />
                    Switch to Helium
                  </button>
                </>
              ) : (
                <>
                  <p>Observe the helium spectrum. Note the yellow, green, and blue lines.</p>
                  <button className="action-btn success" onClick={handleObserveHelium}>
                    <Eye size={16} />
                    Mark as Observed
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 3-4: Neon */}
          {heliumObserved && !neonObserved && (
            <div className="step-card">
              <div className="step-header">
                <div className="step-header-left">
                  <span className="step-num">Step 3–4</span>
                  <span className="step-title">Observe Neon/Argon Spectrum</span>
                </div>
                {stepInfoContent[selectedGas !== 'neon' ? 3 : 4] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(selectedGas !== 'neon' ? 3 : 4); }}
                  >
                    ?
                  </button>
                )}
              </div>
              {selectedGas !== 'neon' ? (
                <>
                  <p>Switch to the neon or argon discharge tube to observe its characteristic spectrum.</p>
                  <button className="action-btn" onClick={handleSwitchToNeon}>
                    <ArrowRight size={16} />
                    Switch to Neon/Argon
                  </button>
                </>
              ) : (
                <>
                  <p>Observe the neon/argon spectrum. Count the lines and note colours and spacing.</p>
                  <button className="action-btn success" onClick={handleObserveNeon}>
                    <Eye size={16} />
                    Mark as Observed
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 5: Record Observations */}
          {heliumObserved && neonObserved && !observationsRecorded && (
            <div className="step-card">
              <div className="step-header">
                <div className="step-header-left">
                  <span className="step-num">Step 5</span>
                  <span className="step-title">Record Observations</span>
                </div>
                {stepInfoContent[5] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(5); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <p>Compare all three spectra and record your observations.</p>
              <button className="action-btn success" onClick={handleRecordObservations}>
                <Check size={16} />
                Complete Observations
              </button>
            </div>
          )}
        </div>

        {/* Comparison Table */}
        <div className="comparison-table">
          <h4>Spectrum Comparison</h4>
          <table>
            <thead>
              <tr>
                <th>Element</th>
                <th>Electrons</th>
                <th>Appearance</th>
                <th>Complexity</th>
                <th>Observed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Hydrogen (H)</td>
                <td>1</td>
                <td>4 distinct lines</td>
                <td>Simplest</td>
                <td><Check size={14} className="check-icon" /></td>
              </tr>
              <tr className={!heliumObserved ? 'pending' : ''}>
                <td>Helium (He)</td>
                <td>2</td>
                <td>7 lines (Purple, Blue, Green, Yellow, Red)</td>
                <td>More complex</td>
                <td>{heliumObserved ? <Check size={14} className="check-icon" /> : '-'}</td>
              </tr>
              <tr className={!neonObserved ? 'pending' : ''}>
                <td>Neon/Argon</td>
                <td>10/18</td>
                <td>Many closely-spaced lines</td>
                <td>Complex</td>
                <td>{neonObserved ? <Check size={14} className="check-icon" /> : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Completion */}
        {observationsRecorded && (
          <div className="completion-panel">
            <Check size={24} />
            <div>
              <h4>Experiment Complete!</h4>
              <div className="conclusion">
                <h5>Key Observations:</h5>
                <ul>
                  <li><strong>Hydrogen:</strong> Simplest spectrum - only 4 visible lines (Balmer series)</li>
                  <li><strong>Helium:</strong> 7 visible lines due to 2 electrons and multiple possible transitions</li>
                  <li><strong>Neon/Argon:</strong> Complex spectrum with many closely-spaced lines</li>
                </ul>
                <h5>Why Different Spectra?</h5>
                <p>
                  Each element has a unique arrangement of electrons in discrete energy levels.
                  When electrons transition between these levels, they emit photons of specific
                  wavelengths. This creates a unique spectral "fingerprint" for each element -
                  the basis of atomic emission spectroscopy.
                </p>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .exp1-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp1-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp1-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp1-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp1-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp1-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp1-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp1-step-number.done { background: var(--secondary); color: white; }
          .exp1-step-number.current { background: var(--primary); color: white; }
          .exp1-step-content { flex: 1; min-width: 0; }
          .exp1-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp1-step-title.highlight { color: var(--primary-light); }
          .exp1-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp1-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp1-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp1-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp1-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp1-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .comparison-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 700px;
          }

          .equipment-row {
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
            justify-content: center;
            align-items: flex-start;
          }

          .gas-info-panel {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            text-align: center;
          }

          .gas-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin-bottom: 8px;
          }

          .gas-name {
            font-size: 18px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .gas-color {
            font-size: 13px;
            font-weight: 500;
          }

          .gas-description {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .line-count {
            font-size: 11px;
            color: var(--text-muted);
            background: var(--bg-dark);
            padding: 4px 10px;
            border-radius: 12px;
          }

          .steps-panel {
            width: 100%;
          }

          .step-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .step-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
          }

          .step-header-left {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .step-num {
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }

          .step-title {
            font-size: 14px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .step-card p {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .comparison-table {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .comparison-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }

          th, td {
            padding: 10px 8px;
            text-align: center;
            border: 1px solid var(--border);
          }

          th {
            background: var(--bg-dark);
            color: var(--text-secondary);
            font-weight: 600;
          }

          td {
            color: var(--text-primary);
          }

          tr.pending td {
            color: var(--text-muted);
          }

          .check-icon {
            color: #00A86B;
          }

          .completion-panel {
            display: flex;
            gap: 16px;
            padding: 20px;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            border-radius: 12px;
            color: #00A86B;
            width: 100%;
          }

          .completion-panel h4 {
            margin: 0 0 12px 0;
          }

          .conclusion {
            color: var(--text-primary);
          }

          .conclusion h5 {
            margin: 12px 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .conclusion ul {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            line-height: 1.8;
          }

          .conclusion p {
            margin: 8px 0 0 0;
            font-size: 12px;
            line-height: 1.6;
            color: var(--text-secondary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp1"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
